import { BEACHES } from '../data/beaches';

export const TZ = 'Asia/Nicosia';
const DAYS = 7;

type Series = (number | null)[];

interface MarineResponse {
  hourly: {
    time: string[];
    wave_height: Series;
    wave_direction: Series;
    wave_period: Series;
    wind_wave_height: Series;
    swell_wave_height: Series;
  };
}

interface WeatherResponse {
  hourly: {
    time: string[];
    wind_speed_10m: Series;
    wind_gusts_10m: Series;
    wind_direction_10m: Series;
    temperature_2m: Series;
  };
}

export interface ForecastData {
  /** one entry per beach, same order as BEACHES */
  marine: MarineResponse[];
  weather: WeatherResponse[];
  /** shared hourly timestamps, local to TZ, e.g. "2026-08-06T14:00" */
  times: string[];
}

const asArray = <T,>(v: T | T[]): T[] => (Array.isArray(v) ? v : [v]);

/**
 * One batched request per API — Open-Meteo accepts comma-separated coordinate
 * lists and returns an array of results in the same order.
 */
export async function fetchForecast(signal?: AbortSignal): Promise<ForecastData> {
  const lats = BEACHES.map((b) => b.mLat).join(',');
  const lons = BEACHES.map((b) => b.mLon).join(',');
  const wlats = BEACHES.map((b) => b.lat).join(',');
  const wlons = BEACHES.map((b) => b.lon).join(',');

  const marineUrl =
    `https://marine-api.open-meteo.com/v1/marine?latitude=${lats}&longitude=${lons}` +
    `&hourly=wave_height,wave_direction,wave_period,wind_wave_height,swell_wave_height` +
    `&timezone=${encodeURIComponent(TZ)}&forecast_days=${DAYS}`;
  const weatherUrl =
    `https://api.open-meteo.com/v1/forecast?latitude=${wlats}&longitude=${wlons}` +
    `&hourly=wind_speed_10m,wind_gusts_10m,wind_direction_10m,temperature_2m` +
    `&timezone=${encodeURIComponent(TZ)}&forecast_days=${DAYS}`;

  const [mRes, wRes] = await Promise.all([fetch(marineUrl, { signal }), fetch(weatherUrl, { signal })]);
  if (!mRes.ok) throw new Error(`marine API ${mRes.status}`);
  if (!wRes.ok) throw new Error(`forecast API ${wRes.status}`);

  const marine = asArray<MarineResponse>(await mRes.json());
  const weather = asArray<WeatherResponse>(await wRes.json());

  return { marine, weather, times: marine[0].hourly.time };
}

/** index of the hour closest to "now" in Cyprus local time; falls back to midday */
export function currentHourIndex(times: string[]): number {
  const nowIso = new Intl.DateTimeFormat('sv-SE', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
  })
    .format(new Date())
    .replace(' ', 'T');
  const idx = times.findIndex((t) => t.startsWith(nowIso.slice(0, 13)));
  return idx < 0 ? 12 : idx;
}
